import { useEffect, useRef, useState } from 'react'
import { router } from '@inertiajs/react'
import { Modal, TextInput, Text, Box, Stack, Loader } from '@mantine/core'
import { useDebouncedValue } from '@mantine/hooks'
import { searchStations, haversineMeters } from './_search'
import { useFormat } from './_units'
import { useStations } from './_stations'
import { useI18n } from './_i18n'
import { useLocalePath } from './_localePath'
import { TILE } from './stations/_tiles'
import './_overlay.css'

// Brand navy overlay palette (matches the list cards: TILE.dark surface,
// TILE.mid header band, white text over hairline dividers).
const SURFACE = TILE.dark
const HEADER = TILE.mid
const MUTED = 'rgba(255,255,255,0.6)'
const HOVER = 'rgba(255,255,255,0.10)'

function SearchIcon() {
  return (
    <svg
      width={18}
      height={18}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="11" cy="11" r="7" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  )
}

function subtitle(s) {
  return [s.city_name, s.state_name || s.state_abbr, s.country_name || s.country_code]
    .filter(Boolean)
    .join(', ')
}

function ResultRow({ station, active, coords, onSelect, onHover }) {
  const fmt = useFormat()
  const { t } = useI18n()
  const sub = subtitle(station)
  const dist =
    coords && station.lat != null && station.lng != null
      ? haversineMeters(coords, { lat: station.lat, lng: station.lng })
      : null
  return (
    <Box
      className="tnm-overlay-row"
      role="option"
      aria-selected={active}
      onMouseEnter={onHover}
      onClick={onSelect}
      style={{
        padding: '10px 12px',
        borderRadius: 8,
        cursor: 'pointer',
        color: '#fff',
        background: active ? HOVER : 'transparent',
      }}
    >
      <Text fw={600} lh={1.2} c="#fff">
        {station.name}
      </Text>
      {(sub || dist != null) && (
        <Text size="sm" lh={1.2} mt={2} style={{ color: MUTED }}>
          {sub}
          {sub && dist != null ? ' · ' : ''}
          {dist != null ? t('distance_away', { distance: fmt.distance(dist) }) : ''}
        </Text>
      )}
    </Box>
  )
}

function GroupLabel({ children }) {
  return (
    <Text
      tt="uppercase"
      fw={700}
      fz="0.72rem"
      px={12}
      pt={8}
      pb={2}
      style={{ letterSpacing: 0.6, color: MUTED }}
    >
      {children}
    </Text>
  )
}

export default function SearchOverlay({ opened, onClose }) {
  const { t } = useI18n()
  const localePath = useLocalePath()
  const [query, setQuery] = useState('')
  const [debounced] = useDebouncedValue(query, 300)
  const [result, setResult] = useState({ status: 'ok', tide: [], current: [] })
  const [loading, setLoading] = useState(false)
  const [active, setActive] = useState(0)
  const [coords, setCoords] = useState(null)
  const reqId = useRef(0)
  const geoRequested = useRef(false)

  const { recents } = useStations()

  // Best-effort geolocation once, on first open (proximity ranking + distances).
  useEffect(() => {
    if (!opened || geoRequested.current) return
    geoRequested.current = true
    if (typeof navigator !== 'undefined' && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => {},
        { enableHighAccuracy: false, timeout: 5000, maximumAge: 300000 },
      )
    }
  }, [opened])

  // Reset transient state whenever the overlay closes.
  useEffect(() => {
    if (!opened) {
      setQuery('')
      setResult({ status: 'ok', tide: [], current: [] })
      setLoading(false)
      setActive(0)
    }
  }, [opened])

  // Run the (debounced) search; guard against stale responses.
  useEffect(() => {
    const q = debounced.trim()
    if (q.length < 2) {
      setResult({ status: 'ok', tide: [], current: [] })
      setLoading(false)
      return
    }
    const id = ++reqId.current
    setLoading(true)
    searchStations(q, coords).then((res) => {
      if (id !== reqId.current) return // stale
      setResult(res)
      setLoading(false)
      setActive(0)
    })
  }, [debounced, coords])

  const items = [...result.tide, ...result.current]

  const select = (station) => {
    if (!station) return
    const url =
      station.kind === 'current'
        ? `/current_stations/${station.id}`
        : `/tide_stations/${station.id}`
    onClose()
    router.visit(localePath(url))
  }

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActive((a) => Math.min(a + 1, items.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActive((a) => Math.max(a - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      select(items[active])
    }
  }

  const q = query.trim()
  const showResults = q.length >= 2
  const recentItems = [
    ...(recents.tide || []).map((s) => ({ ...s, kind: 'tide' })),
    ...(recents.current || []).map((s) => ({ ...s, kind: 'current' })),
  ]

  let body
  if (showResults) {
    if (loading && items.length === 0) {
      body = (
        <Box p="md" ta="center">
          <Loader size="sm" color="#fff" />
        </Box>
      )
    } else if (result.status === 'unavailable') {
      body = (
        <Text p="md" ta="center" style={{ color: MUTED }}>
          {t('search_unavailable')}
        </Text>
      )
    } else if (items.length === 0) {
      body = (
        <Text p="md" ta="center" style={{ color: MUTED }}>
          {t('no_stations_found')}
        </Text>
      )
    } else {
      let idx = -1
      const renderGroup = (label, list) =>
        list.length > 0 && (
          <div>
            <GroupLabel>{label}</GroupLabel>
            {list.map((s) => {
              idx += 1
              const i = idx
              return (
                <ResultRow
                  key={`${s.kind}-${s.id}`}
                  station={s}
                  active={i === active}
                  coords={coords}
                  onHover={() => setActive(i)}
                  onSelect={() => select(s)}
                />
              )
            })}
          </div>
        )
      body = (
        <Stack gap={2}>
          {renderGroup(t('tide_stations'), result.tide)}
          {renderGroup(t('current_stations'), result.current)}
        </Stack>
      )
    }
  } else if (recentItems.length > 0) {
    body = (
      <div>
        <GroupLabel>{t('recents')}</GroupLabel>
        {recentItems.map((s) => (
          <ResultRow
            key={`${s.kind}-${s.id}`}
            station={s}
            active={false}
            coords={coords}
            onHover={() => {}}
            onSelect={() => select(s)}
          />
        ))}
      </div>
    )
  } else {
    body = (
      <Text p="md" ta="center" style={{ color: MUTED }}>
        {t('search_prompt')}
      </Text>
    )
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      withCloseButton={false}
      // No visible title (the search field is the header), so name the dialog
      // for screen readers — Mantine forwards this to the role="dialog" content.
      aria-label={t('search_stations')}
      padding={0}
      radius="md"
      size="lg"
      yOffset="10vh"
      transitionProps={{ transition: 'fade', duration: 150 }}
      overlayProps={{ backgroundOpacity: 0.55, blur: 3 }}
      styles={{ content: { background: SURFACE } }}
    >
      {/* Search field in a TILE.mid band, echoing the list-card header. */}
      <Box px="md" py="sm" style={{ background: HEADER, color: '#fff' }}>
        <TextInput
          classNames={{ input: 'tnm-search-input' }}
          data-autofocus
          value={query}
          onChange={(e) => setQuery(e.currentTarget.value)}
          onKeyDown={onKeyDown}
          placeholder={t('search_placeholder')}
          size="md"
          variant="unstyled"
          leftSection={<SearchIcon />}
          aria-label={t('search_stations')}
          styles={{
            input: { color: '#fff', fontWeight: 500 },
            section: { color: '#fff' },
          }}
        />
      </Box>
      <Box style={{ maxHeight: '60vh', overflowY: 'auto' }} py="xs" px={6}>
        {body}
      </Box>
    </Modal>
  )
}
